import type { FC } from "react"
import { useTranslation } from "react-i18next"
import { Box, Stack, Typography, useTheme } from "@mui/material"

import type { SpanDatum } from "types/spans.types"

import { formatDecimalValues } from "helpers/utils/common"
import { getNumericFormatter } from "helpers/formatters/plots"
import { getIntensity } from "helpers/utils/limitingSpanColor"
import useSpanFrequencyColors from "helpers/hooks/useSpanFrequencyColors"
import { getLastLimiterNote } from "widgets/plots/LimitingSpansHeatmap/helpers"
import SeverityDot from "widgets/plots/SeverityDot"

// Name and length come from the span's GeoJSON feature, not from telemetry.
export interface SpanGeoMeta {
  name: string | null
  length: number | null
}

interface SummaryCardBaseProps {
  // Absent in `block` before anything is selected. The card still renders, so
  // the ribbon under it doesn't shift once a selection arrives.
  datum?: SpanDatum
  meta?: SpanGeoMeta
  // `overlay` floats over the map on a wide screen: compact, two rows. `block`
  // sits under the map on a phone, where it's the only readout — touch has no
  // hover, so it carries the span's identity and both methods on its own.
  variant: "overlay" | "block"
}

interface FrequencySummaryCardProps extends SummaryCardBaseProps {
  mode?: "frequency"
  // The scale every percentage here is read against.
  maxPct: number
  // Per-method totals over the window, for the "{count} of {total}" line.
  totals: { aar: number; dlr: number }
  // Methods whose counts failed. Their tile says so rather than showing `0.0%`,
  // which would be a figure we never measured.
  methodErrors?: { aar: boolean; dlr: boolean }
  // Methods whose last reported limiting span is this one. The map rings and the
  // ribbon's white arrow say it graphically; here it gets said in words.
  lastLimiterMethods?: ("aar" | "dlr")[]
  // The window the counts cover, preformatted. `block` only.
  dateRangeLabel?: string
}

/**
 * Drops every ampacity figure and leaves the span's name, for a card with
 * geometry but no counts. The frequency inputs are `never` so passing one is
 * an error rather than a number the card silently drops.
 */
interface NeutralSummaryCardProps extends SummaryCardBaseProps {
  mode: "neutral"
  maxPct?: never
  totals?: never
  methodErrors?: never
  lastLimiterMethods?: never
  dateRangeLabel?: never
}

export type SpanSummaryCardProps = FrequencySummaryCardProps | NeutralSummaryCardProps

const METHODS = ["aar", "dlr"] as const

const SpanSummaryCard: FC<SpanSummaryCardProps> = (props) => {
  const { datum, meta, variant } = props
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const { colorFor } = useSpanFrequencyColors()
  const locale = i18n.resolvedLanguage ?? "en"
  // The counts next to it are localized, so the percentage has to be too.
  const formatPct = getNumericFormatter(locale, 1)

  // The overlay only ever appears with a span under the pointer.
  if (variant === "overlay" && !datum) return null

  const length = meta?.length != null ? formatDecimalValues(meta.length, locale, 1) : null
  const name = meta?.name ?? t("generic.UNNAMED")

  const frame = {
    border: "1px solid",
    borderColor: theme.palette.neutral[400],
    borderRadius: 1,
    px: 1.5,
    py: 1.25,
  }

  const overlayFrame = {
    ...theme.mixins.floatingSurface,
    maxWidth: 220,
    px: 1.5,
    py: 1,
    boxShadow: 3,
  }

  // All identity says, shared by both variants so they can't drift.
  const identityHeader = datum && (
    <>
      <Typography variant="subtitle2" fontWeight={700} color="text.primary" noWrap>
        {t("spans.ampacity.hover.span", { index: datum.spanIndex })}
      </Typography>

      {/* Wraps rather than truncates: the id is the longest thing here and the
          one you might need to read in full. */}
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 0.25,
          color: "text.secondary",
          whiteSpace: "normal",
          wordBreak: "break-word",
        }}
      >
        {t(
          length != null
            ? "spans.ampacity.card.identity"
            : "spans.ampacity.card.identityNoLength",
          { id: datum.spanId ?? "—", name, length },
        )}
      </Typography>
    </>
  )

  if (props.mode === "neutral") {
    // Nothing was measured, so it names the span and stops. Tiles, dots and
    // the last-limiter note would each be a claim about telemetry.
    if (variant === "overlay") {
      return <Box sx={overlayFrame}>{identityHeader}</Box>
    }
    return (
      <Box sx={frame}>
        {identityHeader ?? (
          <>
            {/* `empty.*` is about limiting-span data. What is missing here is
                only a selection. */}
            <Typography variant="body2" color="text.secondary">
              {t("spans.ampacity.neutral.emptyTitle")}
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block">
              {t("spans.ampacity.neutral.emptyHint")}
            </Typography>
          </>
        )}
      </Box>
    )
  }

  const { maxPct, totals, methodErrors, lastLimiterMethods, dateRangeLabel } = props

  const lastLimiterNote = getLastLimiterNote(lastLimiterMethods ?? [], t)

  if (variant === "overlay" && datum) {
    const title =
      datum.spanId != null
        ? t("spans.ampacity.hover.spanWithId", {
            index: datum.spanIndex,
            id: datum.spanId,
          })
        : t("spans.ampacity.hover.span", { index: datum.spanIndex })

    return (
      <Box sx={overlayFrame}>
        <Typography
          variant="caption"
          fontWeight={700}
          display="block"
          mb={0.5}
          color="text.primary"
        >
          {title}
        </Typography>
        {METHODS.map((key) => (
          <Stack
            key={key}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <Typography variant="caption" color="text.secondary">
              {t(
                key === "dlr"
                  ? "powerlines.LIMITING_SPAN_DLR"
                  : "powerlines.LIMITING_SPAN_AAR",
              )}
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <SeverityDot color={colorFor(getIntensity(datum[key].pct, maxPct))} />
              <Typography
                variant="caption"
                fontWeight={600}
                color={methodErrors?.[key] ? "text.disabled" : "text.secondary"}
              >
                {methodErrors?.[key]
                  ? t("spans.ampacity.lane.unavailableShort")
                  : t("spans.ampacity.hover.pctCount", {
                      pct: formatPct(datum[key].pct),
                      value: datum[key].count.toLocaleString(locale),
                    })}
              </Typography>
            </Stack>
          </Stack>
        ))}
        {lastLimiterNote && (
          <Typography
            variant="caption"
            display="block"
            mt={0.5}
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            {lastLimiterNote}
          </Typography>
        )}
      </Box>
    )
  }

  // Nothing selected and nothing to select. Zero rows fits "no span ever
  // limited", "a gap in reporting" and "a range older than the worker" equally
  // well, so the copy stays about the data and never about the line.
  if (!datum) {
    return (
      <Box sx={frame}>
        <Typography variant="body2" color="text.secondary">
          {t("spans.ampacity.empty.title")}
        </Typography>
        <Typography variant="caption" color="text.disabled" display="block">
          {t("spans.ampacity.empty.hint")}
        </Typography>
        {dateRangeLabel && (
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            {dateRangeLabel}
          </Typography>
        )}
      </Box>
    )
  }

  return (
    <Box sx={frame}>
      {identityHeader}

      {lastLimiterNote && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.5,
            color: "text.secondary",
            fontStyle: "italic",
            whiteSpace: "normal",
          }}
        >
          {lastLimiterNote}
        </Typography>
      )}

      <Stack direction="row" gap={1} mt={1}>
        {METHODS.map((key) => (
          <Box
            key={key}
            sx={{
              flex: 1,
              minWidth: 0,
              px: 1,
              py: 0.75,
              borderRadius: 1,
              bgcolor: "background.default",
            }}
          >
            <Stack direction="row" alignItems="center" gap={0.75}>
              <SeverityDot color={colorFor(getIntensity(datum[key].pct, maxPct))} />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t(
                  key === "dlr"
                    ? "powerlines.LIMITING_SPAN_DLR"
                    : "powerlines.LIMITING_SPAN_AAR",
                )}
              </Typography>
            </Stack>
            {methodErrors?.[key] ? (
              // No counts, so no percentage: `0.0%` would be a made-up measurement.
              <Typography
                variant="body2"
                color="text.disabled"
                sx={{ mt: 0.5, mb: 0.25 }}
              >
                {t("spans.ampacity.lane.unavailableShort")}
              </Typography>
            ) : (
              <>
                <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, lineHeight: 1.2 }}>
                  {formatPct(datum[key].pct)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("spans.ampacity.card.countOfTotal", {
                    value: datum[key].count.toLocaleString(locale),
                    total: totals[key].toLocaleString(locale),
                  })}
                </Typography>
              </>
            )}
          </Box>
        ))}
      </Stack>

      {/* The counts only mean something against their window, and on a phone
          this card is the only place it's written down. */}
      {dateRangeLabel && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.75,
            color: "text.secondary",
            whiteSpace: "normal",
          }}
        >
          {dateRangeLabel}
        </Typography>
      )}
    </Box>
  )
}

export default SpanSummaryCard
